import type {
  PackageDetailFragmentFragment,
  PackageDetailsFilter,
  ReservationDetailFragmentFragment,
} from "@/__generated__/graphql";
import {
  PackageDepartmentEnum,
  PackageRecurrenceEnum,
  ServiceTypeEnum,
} from "@/__generated__/graphql";
import type { VisitDetailData } from "@/components/shared/visits/visit-detail";
import type { PetCareServiceType, ServiceDetailKey } from "@/constants/booking";
import type {
  BookingInfoFormData,
  BookingInfoFormReturn,
} from "@/containers/booking-info/useBookingInfoForm";
import type { BabySitterFormData } from "@/containers/profile/service-profile/service-profile-form/baby/useBabyProfileForm";
import type { ElderCareFormData } from "@/containers/profile/service-profile/service-profile-form/elder/useElderCareForm";
import type { PetProfileFormData } from "@/containers/profile/service-profile/service-profile-form/pet/usePetProfileForm";
import ServiceDetailData from "@/containers/service-detail/service-detail.json";
import { today } from "@/lib/helpers/date";
import { log } from "@/lib/store-middleware";
import type { AppliedPromo, ServiceDetail, TimeSlot, Worker } from "@/types/booking";
import { PackageFrequencyGroupEnum } from "@/types/booking";
import type { Address } from "@/types/users";
import { addDays } from "date-fns";
import { create } from "zustand";

export function getRebookAdHocState(duration: number) {
  return {
    currentPath: "/booking/home-cleaning/select-slot",
    department: PackageDepartmentEnum.HomeCleaning,
    selectedPackageIds: [],
    selectedAddress: null,
    selectedWorkers: [],
    packageDetailsFilter: {
      duration,
      serviceTypes: [ServiceTypeEnum.HomeCleaning],
      repeatEvery: [PackageRecurrenceEnum.AdHoc],
      repeatEveryTimes: 0,
      frontEndFrequency: PackageFrequencyGroupEnum.AD_HOC,
    },
    startDate: new Date(),
    endDate: addDays(new Date(), 6),
    isRebook: true,
  };
}

export const defaultServiceDetails = {
  title: "",
  bookingTotal: 0,
  descriptions: [],
  image: "",
  detailImages: [],
  faqs: [],
  startPrice: 0,
  guarantee: true,
};

export type AddToCalendarProps = {
  title: string;
  description: string;
  startTime?: string;
  endTime?: string;
  location: string;
  visits: VisitDetailData[];
};

export type ConfirmedWorker = {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  workerRating: number;
};

export type BookingState = {
  // state "department" is more frequently used so move it to out.
  packageDetailsFilter: Omit<PackageDetailsFilter, "department"> & {
    frontEndFrequency?: PackageFrequencyGroupEnum;
  };
  // general
  department: PackageDepartmentEnum;
  serviceDetails: Record<ServiceDetailKey, ServiceDetail>;
  currentPath: string;
  isRebook: boolean;
  additionalNotes?: string;
  // aircon Booking
  airconSalesModalOpen: boolean;
  // select slot
  startDate: Date;
  endDate: Date;
  timeSlots: Record<string, TimeSlot[]> | null;
  selectedSlot: TimeSlot | null;
  reservationTimeStamp: number;
  selectedWorkers: Worker[];
  postalCode: string;
  selectedAddress: Address | null;
  packageDetails: PackageDetailFragmentFragment[];
  selectedPackageIds: string[];
  reservations: ReservationDetailFragmentFragment[];
  creationReason?: string;
  // booking info
  bookingInfoFormReturn: BookingInfoFormReturn | null;
  bookingInfo: BookingInfoFormData | null;
  promo: AppliedPromo | null;
  // confirm
  confirmedWorker: ConfirmedWorker | null;
  packageId: string | null;
  // verticals
  homeBeautyFreeGelServiceIds: string[]; // to identify services with Free Gel Removal Checked
  serviceDurationModalOpen: boolean;
  choosePackageLoading: boolean;
  chooseServicePackageModal: boolean;
  slotExpiredModalOpen: boolean;
  // add to calendar
  addToCalendarModalOpen: boolean;
  eventAdded: string;
  // indicate booking is complete and reset state
  completedBooking: boolean;
  // home massage
  homeMassagePackageModalOpen: boolean;
  // pet care
  petProfileInfo: (PetProfileFormData & { id: string }) | null;
  petCarePackageModalOpen: PetCareServiceType | null;
  isEmptyErrorPetProfile: boolean;
  // elder-care
  elderCareProfileInfo: (ElderCareFormData & { id: string }) | null;
  isEmptyErrorElderProfile: boolean;
  selectedServiceProfileId: string | null;
  openClientServiceProfileForm: boolean;
  // deep-cleaning
  totalCleaner: number;
  //babysitter
  babyProfileInfo: (BabySitterFormData & { id: string }) | null;
  babysitterDurationModal: boolean;
  babysitterAcknowledge: boolean;
};

export const initialState: BookingState = {
  packageDetailsFilter: {},
  serviceDetails: ServiceDetailData,
  // general
  department: PackageDepartmentEnum.HomeCleaning,
  currentPath: "",
  slotExpiredModalOpen: false,
  choosePackageLoading: false,
  isRebook: false,
  additionalNotes: "",
  // aircon Booking
  airconSalesModalOpen: false,
  // select slot
  timeSlots: null,
  selectedSlot: null,
  postalCode: "",
  selectedAddress: null,
  startDate: today(),
  endDate: addDays(today(), 6),
  reservationTimeStamp: 0,
  selectedWorkers: [],
  packageDetails: [],
  selectedPackageIds: [],
  reservations: [],
  // confirm
  confirmedWorker: null,
  packageId: null,
  // booking info
  promo: null,
  bookingInfoFormReturn: null,
  bookingInfo: null,
  // home-cleaning
  serviceDurationModalOpen: false,
  chooseServicePackageModal: false,
  homeBeautyFreeGelServiceIds: [],
  // home-massage
  homeMassagePackageModalOpen: false,
  // add to calendar
  addToCalendarModalOpen: false,
  eventAdded: "",
  completedBooking: false,
  // pet care
  petProfileInfo: null,
  petCarePackageModalOpen: null,
  isEmptyErrorPetProfile: false,
  // elder-care
  elderCareProfileInfo: null,
  isEmptyErrorElderProfile: false,
  selectedServiceProfileId: null,
  openClientServiceProfileForm: false,
  // deep-cleaning
  totalCleaner: 1,
  // babysitter
  babyProfileInfo: null,
  babysitterDurationModal: false,
  babysitterAcknowledge: false,
};

export const useBookingState = create(
  log<{
    data: BookingState;
    setBookingState(params: Partial<BookingState>): void;
    resetBookingState(): void;
  }>((set, get) => ({
    data: initialState,
    setBookingState: (params: Partial<BookingState>) => {
      set({ data: { ...get().data, ...params } });
    },
    resetBookingState: () => {
      set({ data: initialState });
    },
  })),
);
